// «Новые лиды из Telegram» — dashboard digest of the candidate review
// queue: the pending counter plus the last few candidates (name/excerpt).
// Click opens /telegram-candidates. The candidates API is owner/admin-only;
// for other roles the widget renders a quiet empty state instead of a
// guaranteed 403 (same gate as the full queue page).

import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { ArrowUpRight, Send } from 'lucide-react';

import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';
import { useTelegramCandidates } from '../../features/telegram-candidates/hooks/useTelegramCandidates.js';
import { useCurrentScope } from '../../shared/scope/useCurrentScope.js';
import type { TelegramCandidateItem } from '../../features/telegram-candidates/api/telegram-candidates.client.js';

const PREVIEW_LIMIT = 3;

/** Short first line of a candidate: who it is about, or the type label. */
function candidateTitle(candidate: TelegramCandidateItem): string {
  const payload = candidate.finalPayload;
  if (payload?.type === 'contact') {
    return [payload.firstName, payload.lastName].filter(Boolean).join(' ');
  }
  if (payload?.type === 'task') {
    return payload.title;
  }
  if (payload?.type === 'note' || payload?.type === 'contact_interaction') {
    const body = 'body' in payload ? (payload.body ?? '') : '';
    if (body.trim() !== '') return body.trim();
    if ('subject' in payload && payload.subject !== null && payload.subject.trim() !== '') {
      return payload.subject.trim();
    }
  }
  return candidate.typeLabel;
}

function candidateExcerpt(candidate: TelegramCandidateItem): string | null {
  const raw = candidate.sourceExcerpt?.trim() ?? '';
  return raw === '' ? null : raw;
}

export interface TelegramLeadsWidgetProps {
  className?: string;
  testId?: string;
  onOpenQueue?: () => void;
}

export function TelegramLeadsWidget({
  className,
  testId,
  onOpenQueue,
}: TelegramLeadsWidgetProps): ReactElement {
  const { t } = useTranslation();
  const scope = useCurrentScope();
  // Owner/admin gate mirrors usePlannerEvents: the query stays disabled for
  // other roles, and the panel shows a quiet empty state.
  const canReview = scope?.role === 'owner' || scope?.role === 'admin';
  const { data, isLoading } = useTelegramCandidates({ enabled: canReview });

  const items = data?.items ?? [];
  const pending = items.filter((item) => item.status === 'pending');
  const latest = [...pending]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, PREVIEW_LIMIT);

  const canOpen = typeof onOpenQueue === 'function';
  const widgetContainerProps = {
    title: t('crm.dashboard.telegram_leads.title'),
    icon: Send,
    iconColor: '#229ED9',
    minHeight: 140,
    noPadding: true,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
    headerRight: (
      <button
        type="button"
        {...(canOpen ? { onClick: onOpenQueue } : {})}
        disabled={!canOpen}
        className="flex items-center gap-1 font-medium"
        style={{
          fontSize: 11,
          color: canOpen
            ? 'var(--ant-color-primary-text, #b45309)'
            : 'var(--ant-color-text-tertiary, #6d6963)',
          cursor: canOpen ? 'pointer' : 'default',
          transition: 'opacity 110ms ease',
        }}
      >
        {t('crm.dashboard.telegram_leads.open')} <ArrowUpRight size={10} />
      </button>
    ),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      {isLoading ? (
        <div
          className="animate-pulse"
          style={{ padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}
        >
          {[1, 2, 3].map((index) => (
            <div
              key={index}
              style={{
                height: 26,
                borderRadius: 'var(--ant-border-radius-sm, 6px)',
                background: 'var(--g-fill, #f5f1eb)',
              }}
            />
          ))}
        </div>
      ) : !canReview || pending.length === 0 ? (
        <div
          className="flex items-center justify-center gap-2"
          style={{ padding: '18px 16px', opacity: 0.6 }}
        >
          <Send size={16} style={{ color: '#229ED9' }} />
          <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
            {!canReview
              ? t('crm.dashboard.telegram_leads.no_access')
              : t('crm.dashboard.telegram_leads.empty')}
          </span>
        </div>
      ) : (
        <div data-testid="telegram-leads-body" className="flex flex-col">
          <div className="flex items-baseline gap-2 px-3.5 pt-3" data-testid="telegram-leads-count">
            <span
              style={{
                fontSize: 22,
                fontWeight: 600,
                letterSpacing: '-0.02em',
                color: 'var(--ant-color-text, #292524)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {pending.length}
            </span>
            <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary, #6d6963)' }}>
              {t('crm.dashboard.telegram_leads.pending')}
            </span>
          </div>
          <div className="flex flex-col py-2">
            {latest.map((candidate) => {
              const excerpt = candidateExcerpt(candidate);
              return (
                <button
                  key={candidate.id}
                  type="button"
                  {...(canOpen ? { onClick: onOpenQueue } : {})}
                  disabled={!canOpen}
                  className="crm-hover-fill flex w-full items-center gap-2.5 border-0 bg-transparent px-3.5 py-2 text-left"
                  style={{ cursor: canOpen ? 'pointer' : 'default' }}
                >
                  <span
                    className="flex h-6 w-6 shrink-0 items-center justify-center"
                    style={{
                      borderRadius: 'var(--ant-border-radius-sm, 6px)',
                      border: '1px solid color-mix(in srgb, #229ED9 45%, transparent)',
                      background: 'color-mix(in srgb, #229ED9 10%, transparent)',
                      color: '#229ED9',
                    }}
                  >
                    <Send size={11} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className="block truncate"
                      style={{
                        fontSize: 12.5,
                        fontWeight: 550,
                        color: 'var(--ant-color-text, #292524)',
                        lineHeight: 1.3,
                      }}
                    >
                      {candidateTitle(candidate)}
                    </span>
                    {excerpt ? (
                      <span
                        className="block truncate"
                        style={{
                          fontSize: 11,
                          color: 'var(--ant-color-text-tertiary, #6d6963)',
                          lineHeight: 1.3,
                        }}
                      >
                        {excerpt}
                      </span>
                    ) : null}
                  </span>
                  <span
                    className="shrink-0 font-mono"
                    style={{
                      fontSize: 10.5,
                      color: 'var(--ant-color-text-tertiary, #6d6963)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {candidate.confidence}%
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </WidgetContainer>
  );
}
